import { Injectable, OnInit } from '@angular/core';
import html2canvas from 'html2canvas';
import { Map } from 'maplibre-gl';
import { MapConfig } from '../../models/map-config';
import { TimeFormatter } from '../../utils/time-formatter';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ColorTypeStrings } from '../../models/color-type-strings';
import { HeatmapTypeStrings } from '../../models/heatmap-type-strings';

@Injectable({
  providedIn: 'root'
})
export class ExportMapService {


  map: Map;
  container: HTMLElement;
  mapboxControlsContainerSelector = '.mapboxgl-control-container';
  html2canvasIgnoreAttribute = 'data-html2canvas-ignore';

  constructor() {
  }

  /**
   * Gera a imagem do mapa, coloca em um link de download e força o click para baixar a imagem.
   */
  exportMap(map: Map, mapConfig: MapConfig) {
    let mapContainer: HTMLElement = map.getContainer().closest('.js-map-container');
    this.setMapContainerDimensions(mapContainer, '100%', '100%');
    let dateInterval = this.getDateInterval(mapConfig);

    let zip = new JSZip();
    let textContent = this.getTextFileContent(mapConfig);

    this.addHtml2CanvasIgnore(this.mapboxControlsContainerSelector);
    html2canvas(mapContainer, {scale: 1}).then(canvas => {

      this.removeHtml2CanvasIgnore(this.mapboxControlsContainerSelector);
      let pngContent = canvas.toDataURL().replace('data:image/png;base64,', '');

      zip.file(`mapa-${dateInterval}.png`, pngContent, {base64: true});
      zip.file(`mapa-${dateInterval}.txt`, textContent);
      zip.generateAsync({type: 'blob'}).then(content => {
        saveAs(content, `mapa-${dateInterval}.zip`);
        this.setMapContainerDimensions(mapContainer, 'inherit', 'inherit');
      });
    });
  }

  /**
   * Define a largura e altura do container do elemento MapComponent.
   * @param mapContainer container do mapa com a classe js-map-container
   * @param width valor do atributo css width
   * @param height valor do atributo css height
   */
  setMapContainerDimensions(mapContainer: HTMLElement, width: string, height: string) {
    mapContainer.style.width = width;
    mapContainer.style.height = height;
  }

  /**
   * Obtém uma string do intervalo de tempo (ou um tempo só, para a análise instantânea)
   * que será usada no nome do arquivo png do mapa gerado.
   * Substitui : por _, pois o navegador não aceita primeiro símbolo ao exportar o png.
   * 
   * @returns string do tipo 'data-inicial[ a data-final]'
   */
  private getDateInterval(mapConfig: MapConfig): string {

    return mapConfig.time.tempo
      .map(t => TimeFormatter.toLocaleString(t))
      .map(s => s.replace(/:/g, '_').replace(/\//g, '_'))
      .join(' a ');
  }

  /**
   * Gera o conteúdo do arquivo de metadados do mapa exportado.
   * @returns string do conteúdo do arquivo texto dos metadados do mapa. 
   */
  private getTextFileContent(mapConfig: MapConfig): string {

    let times = this.getDateIntervalLines(mapConfig);
    let percentiles = this.getPercentilesLines(mapConfig);
    let heatmapTypes = this.getHeatmapTypeLines(mapConfig);
    let content = times + percentiles + heatmapTypes;
    return content;
  }

  /**
   * 
   * @param selector 
   */
  addHtml2CanvasIgnore(selector: string) {
    document.querySelector(selector)?.setAttribute(
      this.html2canvasIgnoreAttribute, 'true');
  }

  /**
   * 
   * @param selector 
   */
  removeHtml2CanvasIgnore(selector: string) {
    document.querySelector(selector)?.removeAttribute(this.html2canvasIgnoreAttribute);
  }

  /**
   * Gera as linhas do intervalo de datas do filtro de tempo.
   * @returns linha no formato 'Data:\n-> Tipo: data e hora no formato local'
   */
  private getDateIntervalLines(mapConfig: MapConfig): string {

    return 'Data:' + mapConfig.time.tempo
      .map((te, index) => `\n-> ${index === 0 ? 'Inicial' : 'Final'}: ${TimeFormatter.toLocaleString(te)}`)
      .join('');
  }
  
  /**
   * Gera as linhas dos percentis inferior e superior
   * @returns linha dos percentis no formato '\n\nPercentis:\n-> Tipo: valor numérico'
   */
  private getPercentilesLines(mapConfig: MapConfig): string {

    return '\n\nPercentis:' + mapConfig.layer.percentile
      .map((p, index) => `\n-> ${index === 0 ? 'Inferior' : 'Superior'}: ${p}`)
      .join('');
  }
  
  /**
   * Gera as linhas de texto sobre as camadas ativas e seus tipos de cores
   * @returns string contendo as linhas das camadas no formato '\n-> Camada: Coloração'
   */
  private getHeatmapTypeLines(mapConfig: MapConfig): string {

    let [ estacao, interpolado, vias, rios ] = mapConfig.layer.list.map(item => item.isActive);
    let heatmapTypeSwitches = [ estacao, interpolado, vias, rios ];

    let heatmapTypes = heatmapTypeSwitches.map((switched, ind) => {
      let heatmapColorInd = mapConfig.layer.list[ind].color as number;
      return switched == true ? 
        `\n-> ${HeatmapTypeStrings.types[ind]}: ${ColorTypeStrings.types[heatmapColorInd]}` 
        : '';
    })
    .join('');

    return heatmapTypes.length > 0 ? `\n\nCamadas:${heatmapTypes}` : '';
  }

  /**
   * Callback de remoção deste controle do container do mapbox
   * @param map 
   */
  onRemove(map: Map) {
    this.container.parentNode.removeChild(this.container);
    this.map = undefined;
  }
}
